import React from "react";
import DocumentTitle from "react-document-title";
import { useParams } from "react-router-dom";
import { DefaultButton, Spinner, SpinnerSize } from "@fluentui/react";
import { ContractOnboardingStrings } from "@/screen/Map/contractOnboarding.strings";
import { useContractStepGuard } from "@/screen/ContractManagement/hooks/useContractStepGuard";
import { useSuitabilityStepController } from "./useSuitabilityStepController";
import * as S from "./SuitabilityStepStyles";
import { formatIsoDateTimeToDdMmYyyy } from "@/utils/DateFormatter";

const SuitabilityStepScreen: React.FC = () =>
{
    const { savingsPlanId } = useParams<{ savingsPlanId: string }>();
    const guard = useContractStepGuard(Number(savingsPlanId), ["2.2.2", "2.4.3"]);
    const isUpdateOnly = !guard.isCurrentStep;
    const controller = useSuitabilityStepController(isUpdateOnly ? null : guard.activeStepKey);
    const strings = ContractOnboardingStrings;
    const evaluation = controller.riskEvaluation;

    if(guard.isChecking)
    {
        return(
            <DocumentTitle title="IronIA - Test de idoneidad">
                <S.PageRoot className="ms-Grid-row savings-plans">
                    <Spinner size={SpinnerSize.large} style={{marginTop: "2rem"}} />
                </S.PageRoot>
            </DocumentTitle>
        );
    }

    return(
        <DocumentTitle title="IronIA - Test de idoneidad">
            <S.PageRoot className="ms-Grid-row savings-plans">
                <h1>{strings.suitabilityTitle}</h1>
                {controller.isLoading &&
                (
                    <Spinner size={SpinnerSize.large} style={{marginTop: "2rem"}} />
                )}
                {!controller.isLoading &&
                (
                    <React.Fragment>
                        {isUpdateOnly &&
                        (
                            <S.InfoText>{strings.suitabilityUpdateOnlyNotice}</S.InfoText>
                        )}
                        <S.TableWrap className="rounded grey p40">
                            <table className="full-width">
                                <thead>
                                    <tr>
                                        <th>Nombre y apellidos</th>
                                        <th>Correo electrónico</th>
                                        <th>Menor de edad</th>
                                        <th>Test de idoneidad</th>
                                        <th className="actions"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {controller.rows.map((row) =>
                                    {
                                        const identity = row.identity;
                                        return(
                                            <tr key={identity.id}>
                                                <td>
                                                    {identity.firstName} {identity.lastName} {identity.secondLastName}
                                                </td>
                                                <td>{identity.email}</td>
                                                <td className="text-center">{row.isMinor ? "Sí" : "No"}</td>
                                                <td className="text-center">
                                                    {row.isMinor && strings.suitabilityNotNeeded}
                                                    {!row.isMinor && !row.hasCompletedTest && !row.hasOnlyExpiredTests &&
                                                    (
                                                        <React.Fragment>
                                                            <i className="ironia-icon warning-alert" />
                                                            {" "}No
                                                        </React.Fragment>
                                                    )}
                                                    {!row.isMinor && !row.hasCompletedTest && row.hasOnlyExpiredTests &&
                                                    (
                                                        // Test firmado hace más de 2 años: no cuenta y hay que repetirlo.
                                                        <React.Fragment>
                                                            <i className="ironia-icon warning-alert" />
                                                            {" "}{strings.suitabilityTestExpired}
                                                        </React.Fragment>
                                                    )}
                                                    {!row.isMinor && row.hasCompletedTest &&
                                                    (
                                                        <span>{row.riskLabel || "Sí"}</span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {!row.isMinor && identity.id != null &&
                                                    (
                                                        <React.Fragment>
                                                            <DefaultButton
                                                                className="button mini-primary-button"
                                                                disabled={
                                                                    identity.canActivateSuitabilityTestButton === false
                                                                    || identity.status === "CANCELLED"
                                                                    || controller.isSubmitting
                                                                }
                                                                onClick={() => controller.onStartTest(identity.id as number)}
                                                            >
                                                                {row.hasCompletedTest || row.hasOnlyExpiredTests
                                                                    ? strings.suitabilityUpdateTest
                                                                    : strings.suitabilityPerformTest}
                                                            </DefaultButton>
                                                            {row.suitabilityNextUpdateAt &&
                                                            (
                                                                <div>
                                                                    Podrás actualizarlo a partir del {formatIsoDateTimeToDdMmYyyy(row.suitabilityNextUpdateAt)}
                                                                </div>
                                                            )}
                                                        </React.Fragment>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </S.TableWrap>
                        {evaluation?.planRiskProfile &&
                        (
                            <p className="description">
                                {strings.suitabilityRiskPlanLabel(
                                    evaluation.planRiskProfile.order,
                                    evaluation.planRiskProfile.name
                                )}
                            </p>
                        )}
                        {evaluation?.intervenersRiskProfile &&
                        (
                            <p className="description">
                                {strings.suitabilityRiskIntervenersLabel(
                                    evaluation.intervenersRiskProfile.order,
                                    evaluation.intervenersRiskProfile.name
                                )}
                            </p>
                        )}
                        {controller.allRequiredTestsComplete && evaluation?.isApplicable && !evaluation.isCompatible &&
                        (
                            <S.RiskBlock>
                                <S.Message>{strings.suitabilityIncompatible}</S.Message>
                                <S.AlternativesList>
                                    {(evaluation.compatibleAlternatives || []).map((alternative) =>
                                    (
                                        <S.AlternativeRow key={alternative.id}>
                                            <div>
                                                <strong>{alternative.name}</strong>
                                                <div>
                                                    Riesgo {alternative.riskProfileOrder}/4 {alternative.riskProfileName}
                                                </div>
                                            </div>
                                            <DefaultButton className="button tiny-primary-button"
                                                disabled={controller.isChangingPortfolio || controller.isSubmitting}
                                                onClick={() =>
                                                {
                                                    void controller.onSelectAlternative(alternative);
                                                }}
                                            >
                                                {controller.isChangingPortfolio
                                                    ? <Spinner size={SpinnerSize.small} />
                                                    : strings.suitabilitySelectAlternative}
                                            </DefaultButton>
                                        </S.AlternativeRow>
                                    ))}
                                </S.AlternativesList>
                            </S.RiskBlock>
                        )}
                        {!isUpdateOnly && !controller.allRequiredTestsComplete &&
                        (
                            <S.Message>{strings.suitabilityTestsIncomplete}</S.Message>
                        )}
                        {controller.infoMessage &&
                        (
                            <S.InfoText>{controller.infoMessage}</S.InfoText>
                        )}
                        {controller.errorMessage &&
                        (
                            <S.ErrorText>{controller.errorMessage}</S.ErrorText>
                        )}
                        {controller.riskEvaluationFailed &&
                        (
                            <S.ActionsRow>
                                <DefaultButton className="button tiny-secondary-button"
                                    disabled={controller.isSubmitting || controller.isChangingPortfolio}
                                    onClick={() =>
                                    {
                                        void controller.onRetryRiskEvaluation();
                                    }}
                                >
                                    {strings.suitabilityRetryRiskEvaluation}
                                </DefaultButton>
                            </S.ActionsRow>
                        )}
                        <S.ActionsRow>
                            <DefaultButton className="button tiny-secondary-button"
                                disabled={controller.isSubmitting}
                                onClick={controller.onBackToMap}
                            >
                                {strings.suitabilityBackToMap}
                            </DefaultButton>
                            {!isUpdateOnly &&
                            (
                                <DefaultButton
                                    className="button tiny-primary-button"
                                    disabled={!controller.canAdvance}
                                    onClick={() =>
                                    {
                                        void controller.onAdvance();
                                    }}
                                >
                                    {controller.isSubmitting &&
                                    (
                                        <Spinner size={SpinnerSize.small} className="button-spinner" />
                                    )}
                                    {strings.suitabilityContinue}
                                </DefaultButton>
                            )}
                        </S.ActionsRow>
                    </React.Fragment>
                )}
            </S.PageRoot>
        </DocumentTitle>
    );
};

export default SuitabilityStepScreen;